<script setup lang="ts">
// A nav item's live count (design §8, plan 2A Chunk 6): fetched once, then again
// whenever one of its topics sends a message, through the shared live client. Hidden
// at 0. A failed fetch shows nothing rather than a stale number.
import { liveClient, useLive, useUser, type NavBadge } from '@itc/sdk';
import { computed, ref, watch } from 'vue';

const props = defineProps<{ badge: NavBadge }>();
const user = useUser();
const count = ref(0);
let generation = 0;

async function refresh(): Promise<void> {
  const mine = ++generation;
  try {
    const next = await props.badge.count();
    if (mine === generation) count.value = next;
  } catch {
    if (mine === generation) count.value = 0;
  }
}
void refresh();

const topics = computed(() => (user.user === null ? [] : props.badge.topics(user.user.id, (permission) => user.can(permission))));
// One subscription per topic, each through the shared client. Indexed so the list can't change length under useLive.
for (let index = 0; index < 4; index++) {
  useLive(
    () => topics.value[index] ?? null,
    () => void refresh(),
    { onResync: () => void refresh() },
  );
}
// A change between the first fetch and the subscription sends no message here: read again once it's live.
watch(
  topics,
  (list) => {
    for (const topic of list) {
      void liveClient()
        .whenSubscribed(topic)
        .then(async (granted) => {
          if (granted) await refresh();
        });
    }
  },
  { immediate: true },
);
</script>

<template>
  <span v-if="count > 0" class="itc-rail__badge" :aria-label="badge.label(count)" role="status" data-testid="nav-badge">{{ count > 99 ? '99+' : count }}</span>
</template>
